import React, { useState } from 'react';
import { ChevronLeft, MapPin, Radio, Search, Eye, Settings, X, User, Phone, Layers, Map } from 'lucide-react';
import C from '../../constants/colors';
import { TX_ACTION_META } from '../../constants/meta';
import StatusDot from '../ui/StatusDot';
import StatBadge from '../ui/StatBadge';
import SiteAccessHistory from './SiteAccessHistory';
import HseCoursesView from './HseCoursesView';

function formatTimeAgo(isoString) {
  if (!isoString) return 'Unknown';
  const diff = Date.now() - new Date(isoString).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)  return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function SiteView({ site, company, zones, employees, visitorCards = [], alerts, activeEvents, transactions, onBack, onEdit, onRemove,
  onStartEvent, onEndEvent, onResetStatus, onEmployeeClick, onZoneClick,
  onEditEmployee, onRemoveEmployee, onSimulateScan, onAddZone, onAddEmployee, onViewMap,
  departments, onAddDepartment, onVisitorClick }) {

  const [mainTab, setMainTab] = useState('people'); // 'people' | 'history' | 'siteEmployees' | 'siteVisitors'
  const [filterTab, setFilterTab] = useState('ALL');
  const [filterZone, setFilterZone] = useState(null);
  const [search, setSearch] = useState('');
  const [empSearch, setEmpSearch] = useState('');

  const activeEvent = activeEvents[site.id];
  const siteEmployees = employees.filter(e => e.currentSiteId === site.id);
  const siteDepts = (departments || []).filter(d => d.siteId === site.id);

  // Normalize assigned visitor cards into the same shape as employees.
  // Only visitors who have scanned into a zone are "on site" — others are like offsite.
  // Exception: unassigned cards that fired NEED_HELP are always included so the tally is correct.
  const siteVisitors = visitorCards
    .filter(vc => vc.siteId === site.id && (
      (vc.status === 'ASSIGNED' && vc.currentZoneId) ||
      (vc.emergencyStatus === 'NEED_HELP')
    ))
    .map(vc => ({
      id: vc.id,
      name: vc.visitorName || vc.cardNumber,
      employeeId: vc.cardNumber,
      department: vc.sponsorDepartment || '',
      role: 'VISITOR',
      status: vc.emergencyStatus || 'NOT_RESPONDED',
      currentZoneId: vc.currentZoneId || null,
      currentSiteId: vc.siteId,
      lastUpdate: vc.lastUpdate || null,
      phone: null,
      isVisitor: true,
      visitorCompanyName: vc.visitorCompanyName || null,
    }));

  // All people on site = employees + checked-in visitors
  const allPeople = [...siteEmployees, ...siteVisitors];

  const safe = allPeople.filter(e => e.status === 'SAFE').length;
  const needHelp = allPeople.filter(e => e.status === 'NEED_HELP').length;
  const pending = allPeople.filter(e => e.status === 'NOT_RESPONDED').length;
  const inZone = allPeople.filter(e => e.currentZoneId).length;
  const notInZone = allPeople.filter(e => !e.currentZoneId).length;

  function getFilteredPeople() {
    let list = allPeople;
    if (filterZone) list = list.filter(e => e.currentZoneId === filterZone);
    switch (filterTab) {
      case 'SAFE': list = list.filter(e => e.status === 'SAFE'); break;
      case 'NEED_HELP': list = list.filter(e => e.status === 'NEED_HELP'); break;
      case 'PENDING': list = list.filter(e => e.status === 'NOT_RESPONDED'); break;
      case 'IN_ZONE': list = list.filter(e => e.currentZoneId); break;
      case 'NOT_IN_ZONE': list = list.filter(e => !e.currentZoneId); break;
      default: break;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        e.name.toLowerCase().includes(q) ||
        (e.employeeId || '').toLowerCase().includes(q) ||
        (e.department || '').toLowerCase().includes(q)
      );
    }
    return list;
  }

  const filtered = getFilteredPeople();
  const eventColors = { FIRE: C.orange, LOCKDOWN: C.purple, EVACUATION: C.red, MEDICAL: C.pink, DRILL: C.blue, OTHER: C.amber };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <ChevronLeft size={16} /><span style={{ fontSize: '13px' }}>Back</span>
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary, margin: 0 }}>{site.name}</h2>
            <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '2px' }}>
              {site.address} &nbsp;·&nbsp; {site.floors} floors &nbsp;·&nbsp; capacity {site.totalCapacity}
            </div>
          </div>
          {activeEvent && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ background: (eventColors[activeEvent.eventType] || C.amber)+'22', border: `1px solid ${(eventColors[activeEvent.eventType] || C.amber)}44`, borderRadius: '8px', padding: '5px 12px', display: 'flex', alignItems: 'center', gap: '6px', animation: 'alertPulse 2s infinite' }}>
                <Radio size={12} color={eventColors[activeEvent.eventType] || C.amber} />
                <span style={{ fontSize: '12px', fontWeight: 700, color: eventColors[activeEvent.eventType] || C.amber }}>{activeEvent.eventType} IN PROGRESS</span>
              </div>
              <button onClick={() => onEndEvent(site.id)} style={{ padding: '5px 12px', borderRadius: '8px', border: `1px solid ${C.red}44`, background: C.red+'22', color: C.red, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>End Event</button>
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={() => window.open(`https://www.google.com/maps?q=${site.lat},${site.lng}`,'_blank')} style={{ padding: '6px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '5px' }}><MapPin size={12} />MAP</button>
            {onViewMap && <button onClick={onViewMap} style={{ padding: '6px 14px', borderRadius: '8px', border: `1px solid ${C.blue}44`, background: C.blue+'22', color: C.blue, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '5px' }}><Map size={12} />Site Map</button>}
            <button onClick={onResetStatus} style={{ padding: '6px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>Reset Status</button>
            <button onClick={onEdit} style={{ padding: '6px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>
            <button onClick={onRemove} style={{ padding: '6px 14px', borderRadius: '8px', border: `1px solid ${C.red}44`, background: 'transparent', color: C.red, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>Remove</button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[
            ['On Site', allPeople.length, C.blue],
            ['Safe', safe, C.green],
            ['Need Help', needHelp, C.red],
            ['Pending', pending, C.amber],
            ['In Zone', inZone, C.textSecondary],
            ['Not in Zone', notInZone, C.textMuted],
          ].map(([label, value, color]) => (
            <StatBadge key={label} label={label} value={value} color={color} />
          ))}
        </div>
      </div>

      {/* Main tab switcher */}
      <div style={{ padding: '0 24px', borderBottom: `1px solid ${C.border}`, background: C.surface, display: 'flex', gap: '4px', flexShrink: 0 }}>
        {[['people','Live View'],['history','Access History'],['siteEmployees','Site Employees'],['siteVisitors','Site Visitors'],...(company?.hseTrainingEnabled ? [['hseTraining','HSE Training']] : [])].map(([key, label]) => (
          <button key={key} onClick={() => setMainTab(key)} style={{
            padding: '10px 16px', borderRadius: 0, border: 'none', borderBottom: mainTab === key ? `2px solid ${C.blue}` : '2px solid transparent',
            background: 'transparent', color: mainTab === key ? C.blue : C.textMuted,
            fontSize: '13px', fontWeight: mainTab === key ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit', marginBottom: '-1px',
          }}>{label}</button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        {mainTab === 'hseTraining' && (
          <HseCoursesView companyId={site.companyId} siteId={site.id} />
        )}
        {mainTab === 'history' && (
          <SiteAccessHistory transactions={(transactions || []).filter(tx => tx.siteId === site.id)} />
        )}
        {mainTab === 'siteEmployees' && (() => {
          const registered = employees.filter(e => e.homeSiteId === site.id);
          const displayed = empSearch.trim()
            ? registered.filter(e =>
                e.name.toLowerCase().includes(empSearch.toLowerCase()) ||
                (e.employeeId || '').toLowerCase().includes(empSearch.toLowerCase()) ||
                (e.department || '').toLowerCase().includes(empSearch.toLowerCase())
              )
            : registered;
          return (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <h3 style={{ fontSize: '13px', fontWeight: 600, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Site Employees</h3>
                  <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '4px' }}>{registered.length} employee{registered.length !== 1 ? 's' : ''} registered to {site.name}</div>
                </div>
                <div style={{ position: 'relative' }}>
                  <Search size={13} color={C.textMuted} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                  <input value={empSearch} onChange={e => setEmpSearch(e.target.value)} placeholder="Search..."
                    style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '7px 12px 7px 30px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', width: '200px' }} />
                </div>
              </div>
              <div style={{ background: C.surfaceElevated, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                        {['Status','Name','ID','Dept','Role','Phone','Actions'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayed.map((emp, i) => (
                        <tr key={emp.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                          <td style={{ padding: '10px 12px' }}><StatusDot status={emp.status} /></td>
                          <td style={{ padding: '10px 12px' }}>
                            <button onClick={() => onEmployeeClick(emp.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textPrimary, fontWeight: 600, fontSize: '13px', fontFamily: 'inherit', padding: 0, textAlign: 'left' }}>{emp.name}</button>
                          </td>
                          <td style={{ padding: '10px 12px', fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', color: C.textSecondary }}>{emp.employeeId}</td>
                          <td style={{ padding: '10px 12px', color: C.textSecondary }}>{emp.department || '—'}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{ fontSize: '11px', background: 'rgba(255,255,255,0.06)', borderRadius: '5px', padding: '2px 7px', color: C.textSecondary }}>{emp.role}</span>
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: '12px', color: C.textSecondary, whiteSpace: 'nowrap' }}>{emp.phone || '—'}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button onClick={() => onEmployeeClick(emp.id)} title="View" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}><Eye size={14} /></button>
                              <button onClick={() => onEditEmployee(emp)} title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}><Settings size={14} /></button>
                              <button onClick={() => onRemoveEmployee(emp.id)} title="Archive" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.amber, padding: '4px' }}><X size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {displayed.length === 0 && (
                        <tr><td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: C.textMuted }}>
                          {registered.length === 0 ? 'No employees registered to this site' : 'No employees match the search'}
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}
        {mainTab === 'siteVisitors' && (() => {
          const signedIn = visitorCards.filter(vc => vc.siteId === site.id && vc.status === 'ASSIGNED' && vc.currentZoneId);
          return (
            <div>
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ fontSize: '13px', fontWeight: 600, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Site Visitors</h3>
                <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '4px' }}>{signedIn.length} visitor{signedIn.length !== 1 ? 's' : ''} currently signed in to {site.name}</div>
              </div>
              <div style={{ background: C.surfaceElevated, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                        {['Status','Name','Card #','Zone','Company','Signed In','Actions'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {signedIn.map((vc, i) => {
                        const zone = zones.find(z => z.id === vc.currentZoneId);
                        return (
                          <tr key={vc.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                            <td style={{ padding: '10px 12px' }}><StatusDot status={vc.emergencyStatus || 'NOT_RESPONDED'} /></td>
                            <td style={{ padding: '10px 12px' }}>
                              {onVisitorClick
                                ? <button onClick={() => onVisitorClick(vc.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textPrimary, fontWeight: 600, fontSize: '13px', fontFamily: 'inherit', padding: 0, textAlign: 'left' }}>{vc.visitorName || '—'}</button>
                                : <span style={{ fontWeight: 600 }}>{vc.visitorName || '—'}</span>
                              }
                            </td>
                            <td style={{ padding: '10px 12px', fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', color: C.textSecondary }}>{vc.cardNumber}</td>
                            <td style={{ padding: '10px 12px', color: C.textSecondary }}>{zone?.name || '—'}</td>
                            <td style={{ padding: '10px 12px', color: C.textSecondary }}>{vc.visitorCompanyName || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: '12px', color: C.textMuted, whiteSpace: 'nowrap' }}>{formatTimeAgo(vc.assignedAt || vc.lastUpdate)}</td>
                            <td style={{ padding: '10px 12px' }}>
                              {onVisitorClick && (
                                <button onClick={() => onVisitorClick(vc.id)} title="View" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}><Eye size={14} /></button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {signedIn.length === 0 && (
                        <tr><td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: C.textMuted }}>No visitors currently signed in to this site</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}
        {mainTab === 'people' && <>
        {/* Zone cards */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: 600, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Zones</h3>
            <button onClick={onAddZone} style={{ padding: '5px 12px', borderRadius: '7px', border: `1px solid ${C.blue}44`, background: C.blue+'22', color: C.blue, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>+ Add Zone</button>
          </div>
          <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '8px' }}>
            {zones.map(zone => {
              const zoneCount = allPeople.filter(e => e.currentZoneId === zone.id).length;
              const zoneAlerts = alerts.filter(a => a.zoneId === zone.id && !a.acknowledged);
              const isSelected = filterZone === zone.id;
              const occupancyPct = zone.capacity ? Math.round((zoneCount / zone.capacity) * 100) : null;
              const occColor = occupancyPct === null ? C.blue : occupancyPct >= 80 ? C.red : occupancyPct >= 60 ? C.amber : C.blue;
              return (
                <div key={zone.id} onClick={() => { setFilterZone(isSelected ? null : zone.id); onZoneClick && onZoneClick(zone.id); }}
                  style={{ background: isSelected ? C.blue+'22' : C.surfaceElevated, border: `1px solid ${isSelected ? C.blue : C.border}`, borderRadius: '10px', padding: '12px 16px', minWidth: '150px', cursor: 'pointer', flexShrink: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary, marginBottom: '4px' }}>{zone.name}</div>
                  <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '8px' }}>Floor {zone.floor}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', color: occColor }}>{zoneCount} in zone</span>
                    {occupancyPct !== null && (
                      <span style={{ fontSize: '11px', fontWeight: 600, color: occColor }}>{occupancyPct}%</span>
                    )}
                    {zoneAlerts.length > 0 && <span style={{ fontSize: '11px', color: C.red, fontWeight: 600 }}>⚠ {zoneAlerts.length}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Departments */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: 600, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Departments</h3>
            <button onClick={onAddDepartment} style={{ padding: '5px 12px', borderRadius: '7px', border: `1px solid ${C.blue}44`, background: C.blue+'22', color: C.blue, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>+ Add Department</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {siteDepts.length === 0
              ? <span style={{ fontSize: '12px', color: C.textMuted }}>No departments yet</span>
              : siteDepts.map(d => (
                  <span key={d.id} style={{ padding: '4px 12px', borderRadius: '20px', background: C.surfaceElevated, border: `1px solid ${C.border}`, fontSize: '12px', color: C.textSecondary }}>
                    {d.name}
                  </span>
                ))
            }
          </div>
        </div>

        {/* Employee table */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: 600, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>People on Site</h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <Search size={13} color={C.textMuted} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
                  style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '7px 12px 7px 30px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', width: '180px' }} />
              </div>
              <button onClick={onAddEmployee} style={{ padding: '7px 12px', borderRadius: '7px', border: `1px solid ${C.green}44`, background: C.green+'22', color: C.green, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>+ Add Employee</button>
            </div>
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', flexWrap: 'wrap' }}>
            {[['ALL','All'],['SAFE','Safe'],['NEED_HELP','Need Help'],['PENDING','Pending']].map(([key,label]) => (
              <button key={key} onClick={() => setFilterTab(key)} style={{
                padding: '5px 12px', borderRadius: '20px', border: `1px solid ${filterTab === key ? C.blue : C.border}`,
                background: filterTab === key ? C.blue+'22' : 'transparent', color: filterTab === key ? C.blue : C.textMuted,
                fontSize: '12px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              }}>{label}</button>
            ))}
          </div>

          {/* Table */}
          <div style={{ background: C.surfaceElevated, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {['Status','Name','ID','Dept','Role','Zone','Last Seen','Phone','Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((emp, i) => {
                    const zone = zones.find(z => z.id === emp.currentZoneId);
                    return (
                      <tr key={emp.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                        <td style={{ padding: '10px 12px' }}>
                          <StatusDot status={emp.status} />
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          {emp.isVisitor ? (
                            <div>
                              <button onClick={() => onVisitorClick && onVisitorClick(emp.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textPrimary, fontWeight: 600, fontSize: '13px', fontFamily: 'inherit', padding: 0, textAlign: 'left' }}>{emp.name}</button>
                              {emp.visitorCompanyName && <div style={{ fontSize: '10px', color: C.textMuted }}>{emp.visitorCompanyName}</div>}
                            </div>
                          ) : (
                            <>
                              <button onClick={() => onEmployeeClick(emp.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textPrimary, fontWeight: 600, fontSize: '13px', fontFamily: 'inherit', padding: 0, textAlign: 'left' }}>{emp.name}</button>
                              {emp.teamSize > 1 && <div style={{ fontSize: '10px', color: C.textMuted }}>+{emp.teamSize-1} team</div>}
                            </>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px', fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', color: C.textSecondary }}>{emp.employeeId}</td>
                        <td style={{ padding: '10px 12px', color: C.textSecondary }}>{emp.department}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{ fontSize: '11px', background: emp.isVisitor ? C.blue+'22' : 'rgba(255,255,255,0.06)', borderRadius: '5px', padding: '2px 7px', color: emp.isVisitor ? C.blue : C.textSecondary }}>{emp.role}</span>
                        </td>
                        <td style={{ padding: '10px 12px', color: emp.currentZoneId ? C.blue : C.textMuted, fontSize: '12px' }}>
                          {zone ? zone.name : '—'}
                        </td>
                        <td style={{ padding: '10px 12px', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: C.textMuted, whiteSpace: 'nowrap' }}>{emp.lastUpdate ? formatTimeAgo(emp.lastUpdate) : '—'}</td>
                        <td style={{ padding: '10px 12px', fontSize: '12px', color: C.textSecondary, whiteSpace: 'nowrap' }}>{emp.phone || '—'}</td>
                        <td style={{ padding: '10px 12px' }}>
                          {emp.isVisitor ? (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button onClick={() => onVisitorClick && onVisitorClick(emp.id)} title="View" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}><Eye size={14} /></button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button onClick={() => onEmployeeClick(emp.id)} title="View" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}><Eye size={14} /></button>
                              <button onClick={() => onEditEmployee(emp)} title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}><Settings size={14} /></button>
                              <button onClick={() => onRemoveEmployee(emp.id)} title="Archive" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.amber, padding: '4px' }}><X size={14} /></button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={9} style={{ padding: '32px', textAlign: 'center', color: C.textMuted }}>No one matches current filter</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Contact bar */}
        <div style={{ marginTop: '20px', background: C.surfaceElevated, borderRadius: '12px', padding: '16px', border: `1px solid ${C.border}`, display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <User size={13} color={C.textMuted} />
            <span style={{ fontSize: '12px', color: C.textSecondary, fontWeight: 600 }}>{site.safetyOfficer?.name}</span>
            <span style={{ fontSize: '12px', color: C.textMuted }}>{site.safetyOfficer?.phone}</span>
          </div>
          {[['Security', site.emergency?.security], ['Ambulance', site.emergency?.ambulance], ['Police', site.emergency?.police], ['Civil Defense', site.emergency?.civilDefense]].map(([label, num]) => num && (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Phone size={11} color={C.textMuted} />
              <span style={{ fontSize: '11px', color: C.textMuted }}>{label}:</span>
              <span style={{ fontSize: '11px', color: C.textSecondary, fontFamily: 'JetBrains Mono, monospace' }}>{num}</span>
            </div>
          ))}
        </div>
        </>}
      </div>
    </div>
  );
}
